import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MTopBar } from "@/components/mobile/MTopBar";
import { MPage, Footer, blueBtn, cardCls } from "@/components/mobile/parts";
import { CustomerSearch } from "@/components/mobile/CustomerSearch";
import { useMobile } from "@/context/MobileContext";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { cachedGet, isNetErr, offlineComandas } from "@/lib/mobile";
import { cn } from "@/lib/utils";

const Label = ({ children }) => <span className="text-sm font-semibold text-slate-700">{children}</span>;

export default function MNewComanda() {
  const [f, setF] = useState({ number: "", table_id: "", people: "", customer_id: null, customer_name: "" });
  const [tables, setTables] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [busy, setBusy] = useState(false);
  const { enqueue, queue } = useMobile();
  const { session } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    cachedGet("/tables").then((t) => setTables(t.filter((x) => !x.comanda_id))).catch(() => {});
    cachedGet("/customers").then(setCustomers).catch(() => {});
  }, []);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const goOffline = async (body) => {
    const open = await cachedGet("/comandas?status=open").catch(() => []);
    if ([...open, ...offlineComandas(queue)].some((c) => c.number === body.number)) return toast.error(`Já existe uma comanda aberta com o número ${body.number}`);
    const temp = `off-${Date.now()}`;
    enqueue({ type: "create", temp_id: temp, payload: body, table_number: tables.find((t) => t.id === body.table_id)?.number || null, label: `Nova comanda #${body.number}`, opened_by: session.user.name, opened_by_id: session.user.id });
    toast.warning("Sem internet: a comanda ficou guardada no celular e será criada quando a conexão voltar");
    return nav(`/m/comanda/${temp}/produtos`, { replace: true });
  };

  const submit = async () => {
    const n = Number(f.number);
    if (!(n >= 1)) return toast.error("Informe o número da comanda");
    const body = { number: n, customer_name: f.customer_name.trim(), customer_id: f.customer_id, table_id: f.table_id || null, people: f.people ? Number(f.people) : null };
    setBusy(true);
    try {
      const { data } = await api.post("/comandas", body);
      nav(`/m/comanda/${data.id}/produtos`, { replace: true });
    } catch (e) {
      if (isNetErr(e)) await goOffline(body);
      else toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <MTopBar title="Adicionar Comanda" back="/m/pdv" />
      <MPage>
        <div className={cn(cardCls, "space-y-4 p-4")}>
          <label className="block space-y-1.5"><Label>Número da Comanda *</Label>
            <Input value={f.number} onChange={set("number")} inputMode="numeric" type="number" min="1" autoFocus placeholder="Ex.: 25" className="h-14 bg-white font-mono text-2xl font-bold" data-testid="m-new-number-input" /></label>
          <label className="block space-y-1.5"><Label>Mesa</Label>
            <select value={f.table_id} onChange={set("table_id")} className="h-12 w-full rounded-md border border-input bg-white px-3 text-base" data-testid="m-new-table-select">
              <option value="">Sem mesa</option>
              {tables.map((t) => <option key={t.id} value={t.id}>Mesa {t.number}{t.name ? ` · ${t.name}` : ""}</option>)}
            </select></label>
          <label className="block space-y-1.5"><Label>Quantidade de Pessoas</Label>
            <Input value={f.people} onChange={set("people")} inputMode="numeric" type="number" min="1" placeholder="Opcional" className="h-12 bg-white text-base" data-testid="m-new-people-input" /></label>
          <div className="space-y-1.5"><Label>Cliente</Label>
            <CustomerSearch customers={customers} value={f} onChange={(v) => setF((x) => ({ ...x, ...v }))} /></div>
        </div>
        <p className="px-1 text-xs text-slate-500">O número não pode repetir enquanto houver outra comanda aberta com ele.</p>
      </MPage>
      <Footer className="border-t bg-white p-4">
        <Button onClick={submit} disabled={busy || !f.number} className={cn("h-14 w-full rounded-xl text-lg", blueBtn)} data-testid="m-new-submit-btn">
          {busy && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}Adicionar
        </Button>
      </Footer>
    </>
  );
}
